/**
 * EditMilestoneModal Component
 * Modal for editing an existing payment milestone
 */

import React, { useState, useEffect } from 'react';
import { paymentMilestoneAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const EditMilestoneModal = ({ isOpen, onClose, onSuccess, milestone, projectId, packagePrice }) => {
  const [formData, setFormData] = useState({
    milestoneName: '',
    targetPercentage: '',
    targetAmount: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Initialize form data when milestone changes
  useEffect(() => {
    if (milestone) {
      setFormData({
        milestoneName: milestone.milestoneName || '',
        targetPercentage: milestone.cumulativePercentage || milestone.targetPercentage || '',
        targetAmount: milestone.cumulativeTarget || milestone.targetAmount || '',
        notes: milestone.notes || ''
      });
    }
  }, [milestone]);

  // Handle percentage change - auto-calculate amount
  const handlePercentageChange = (value) => {
    const percentage = parseFloat(value) || 0;
    const calculatedAmount = packagePrice > 0 ? (packagePrice * percentage / 100).toFixed(2) : '';
    setFormData({
      ...formData,
      targetPercentage: value,
      targetAmount: calculatedAmount
    });
  };

  // Handle amount change - auto-calculate percentage
  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    const calculatedPercentage = packagePrice > 0 ? ((amount / packagePrice) * 100).toFixed(2) : '';
    setFormData({
      ...formData,
      targetAmount: value,
      targetPercentage: calculatedPercentage
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.milestoneName.trim()) {
      toast.error('Please enter a milestone name');
      return;
    }

    if (!formData.targetPercentage || parseFloat(formData.targetPercentage) <= 0) {
      toast.error('Please enter a valid cumulative percentage');
      return;
    }

    if (parseFloat(formData.targetPercentage) > 100) {
      toast.error('Cumulative percentage cannot exceed 100%');
      return;
    }

    setSubmitting(true);

    try {
      // For lock_price milestone, send targetAmount directly
      // For other milestones, send targetPercentage and let backend calculate
      const updateData = {
        milestoneName: formData.milestoneName,
        targetPercentage: parseFloat(formData.targetPercentage),
        notes: formData.notes || null
      };

      // Always include targetAmount so user's direct amount edits are preserved
      if (formData.targetAmount) {
        updateData.targetAmount = parseFloat(formData.targetAmount);
      }

      await paymentMilestoneAPI.update(projectId, milestone.id, updateData);

      toast.success('Milestone updated successfully');
      onSuccess();
    } catch (err) {
      console.error('Error updating milestone:', err);
      toast.error(err.message || 'Failed to update milestone');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this milestone? This cannot be undone.')) {
      return;
    }

    setSubmitting(true);

    try {
      await paymentMilestoneAPI.delete(projectId, milestone.id);
      toast.success('Milestone deleted successfully');
      onSuccess();
    } catch (err) {
      console.error('Error deleting milestone:', err);
      toast.error(err.message || 'Failed to delete milestone');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !milestone) return null;

  const hasPayments = milestone.actualPaidAmount > 0;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop - only for desktop clicking outside */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Edit Milestone
            </h3>
          </div>

          {/* Content - Scrollable */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* Package Price Info */}
            {packagePrice > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Package Price:</span>
                  <span className="font-medium">{formatCurrency(packagePrice)}</span>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Milestone Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milestone Name *
                </label>
                <input
                  type="text"
                  value={formData.milestoneName}
                  onChange={(e) => setFormData({ ...formData, milestoneName: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Target Amount & Percentage - linked fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cumulative Target (RM)
                  </label>
                  <input
                    type="number"
                    value={formData.targetAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    disabled={submitting}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cumulative %
                  </label>
                  <input
                    type="number"
                    value={formData.targetPercentage}
                    onChange={(e) => handlePercentageChange(e.target.value)}
                    disabled={submitting}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Cumulative %: total percentage of package to be paid by this milestone
              </p>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={submitting}
                  rows={2}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                />
              </div>

              {/* Delete Button - Inside content on mobile */}
              {!hasPayments && (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={submitting}
                    className="w-full sm:w-auto px-4 py-3 border border-red-300 rounded-lg text-sm font-medium text-red-600 bg-white hover:bg-red-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
                  >
                    Delete Milestone
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMilestoneModal;
